import { FormGroup, Radio, RadioGroup, TextArea } from "@blueprintjs/core";
import PropTypes from "prop-types";
import React, { Component } from "react";
import classNames from "classnames";
import { useCallback, useState } from "react";
import { THEATRE_CRITERIA_MENU_LIST } from "../../constants";
import styles from "../../styles/SelectByIdPanel.module.scss";
import stylesUpload from "../../styles/UploadCsv.module.scss";
import stylesLoading from "../../styles/FileUpload.module.scss";
import cleanIDSelection from "../../utils/cleanIDSelection";
import DetailsLoading from "./DetailsLoading";
import RightPanel from "./RightPanel";
import { Checked, Copy } from "./LucideIcons";

export default class SelectByIdPanel extends Component {
  state = {
    hasFormSaved: false,
    idInputValue: "",
    selectionTypes: [
      { value: "inclusions", label: "Inclusions" },
      { value: "exclusions", label: "Exclusions" },
    ],
    isProcessing: false,
  };

  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    getSelectedIds: PropTypes.func.isRequired,
    header: PropTypes.string.isRequired,
    selectedCriteriaType: PropTypes.object.isRequired,
    onCriteriaChange: PropTypes.func.isRequired,
    invalidIds: PropTypes.array,
    isLoading: PropTypes.bool,
    primaryButtonProps: PropTypes.array,
    secondaryButtonProps: PropTypes.object,
  };

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      const defaultCriteria =
        THEATRE_CRITERIA_MENU_LIST.length > 0 ? THEATRE_CRITERIA_MENU_LIST[1] : null;

      if (defaultCriteria) {
        this.props.onCriteriaChange(defaultCriteria);
      }
    }

    // Clear TextArea if panel is closed
    if (prevProps.isOpen && !this.props.isOpen) {
      this.setState({ idInputValue: "" });
    }
  }

  handleFormCancel = () => {
    this.props.onClose();
    this.setState({ idInputValue: "" });
  };

  handleInputChange = (event) => {
    if (this.props.getSelectedIds) {
      const rawInput = event.target.value;
      const selection = cleanIDSelection(rawInput);
      this.setState({ idInputValue: rawInput });
      this.props.getSelectedIds(selection);
    }
  };

  handleCriteriaChange = (event) => {
    const selectedCriteriaId = event.target.value;
    const selectedCriteria = THEATRE_CRITERIA_MENU_LIST.find(
      (criteria) => criteria.id === selectedCriteriaId
    );
    const { onCriteriaChange } = this.props;
    if (onCriteriaChange && selectedCriteria) {
      onCriteriaChange(selectedCriteria);
    }
  };
  handleVerify = async () => {
    const { onClick } = this.props.primaryButtonProps[0] || {};
    this.setState({ isProcessing: true });
    if (onClick) {
      await onClick();
    }
    this.setState({ isProcessing: false });
  };

  renderPanelContent = () => {
    if (this.props.isLoading) return <DetailsLoading />;
    const {
      selectedCriteriaType,
      selectionType,
      handleSelectionTypeChange,
      matchedTheatreData,
      unmatchedTheatreData,
    } = this.props;
    const { selectionTypes, isProcessing } = this.state;

    const matchedCount = matchedTheatreData.length;
    const unmatchedCount = unmatchedTheatreData.length;

    // Check if any matched data are screens
    const hasMatchedScreen = matchedTheatreData.some((data) => data.request.screen_id);

    // Check if any unmatched data are screens or theatres
    const hasUnmatchedScreen = unmatchedTheatreData.some((data) => data.request.screen_id);
    const hasUnmatchedTheatre = unmatchedTheatreData.some((data) => data.request.id);
    return (
      <div>
        {isProcessing ? (
          <div className={classNames(stylesLoading.fileContainer, stylesLoading.loadingContainer)}>
            <div className={stylesLoading.loader} />
            <span style={{ width: "150px" }}>Verifying screens</span>
          </div>
        ) : (
          <div>
            <ShowMatchedResults
              matchedCount={matchedCount}
              hasMatchedScreen={hasMatchedScreen}
              unmatchedCount={unmatchedCount}
              hasUnmatchedScreen={hasUnmatchedScreen}
              hasUnmatchedTheatre={hasUnmatchedTheatre}
              unmatchedTheatreData={unmatchedTheatreData}
            />
            {matchedCount === 0 && unmatchedCount === 0 && (
              <div>
                <div className={styles.criteriaTypeContainer}>
                  <label className={styles.criteriaTypeLabel}>Add to</label>
                  <RadioGroup
                    className={styles.criteriaTypeRadioGroup}
                    onChange={(v) =>
                      handleSelectionTypeChange(
                        selectionTypes.find((t) => t.value === v.target.value).value
                      )
                    }
                    selectedValue={selectionType}
                    inline
                  >
                    {selectionTypes.map((t) => (
                      <Radio key={t.value} value={t.value} label={t.label} />
                    ))}
                  </RadioGroup>
                  <label className={styles.criteriaTypeLabel}>ID Type</label>
                  <RadioGroup
                    className={styles.criteriaTypeRadioGroup}
                    onChange={this.handleCriteriaChange}
                    selectedValue={selectedCriteriaType.id}
                    inline
                  >
                    {THEATRE_CRITERIA_MENU_LIST.slice(0, 2).map((criteria) => (
                      <Radio
                        key={criteria.id}
                        value={criteria.id}
                        label={criteria.label === "Screen By ID" ? "Screen" : "Theatre"}
                      />
                    ))}
                  </RadioGroup>
                </div>
                <FormGroup
                  label="IDs:"
                  contentClassName={styles.inputContainer}
                  helperText="Separate multiple IDs with a new line or a comma. This will override your current selection."
                >
                  <TextArea
                    fill
                    rows={15}
                    onChange={this.handleInputChange}
                    value={this.state.idInputValue}
                  />
                </FormGroup>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  render = () => {
    const { isOpen, header, primaryButtonProps, secondaryButtonProps } = this.props;
    return (
      <RightPanel
        isOpen={isOpen}
        onClose={this.handleFormCancel}
        header={header}
        showFooter={true}
        primaryButtonProps={[{ ...primaryButtonProps[0], onClick: this.handleVerify }]}
        secondaryButtonProps={secondaryButtonProps}
      >
        {this.renderPanelContent()}
      </RightPanel>
    );
  };
}

export const ShowMatchedResults = ({
  matchedCount,
  hasMatchedScreen,
  unmatchedCount,
  hasUnmatchedScreen,
  hasUnmatchedTheatre,
  unmatchedTheatreData,
}) => {
  const [copied, setCopied] = useState(false);

  const CopyTableColumnToClipboard = useCallback(async () => {
    const tableData = unmatchedTheatreData
      .map((data) => data.request.id || data.request.screen_id)
      .join(" , ");
    await navigator.clipboard.writeText(tableData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.uploadedFileContainer}>
      {matchedCount > 0 && (
        <>
          <h4>
            {matchedCount}{" "}
            {hasMatchedScreen
              ? `Screen${matchedCount > 1 ? "s" : ""}`
              : `Theatre${matchedCount > 1 ? "s" : ""}`}{" "}
            found
          </h4>
        </>
      )}
      {unmatchedCount > 0 && (
        <>
          <h4 className={stylesUpload.invalidCount}>
            No matches found for the following {unmatchedCount}{" "}
            {hasUnmatchedScreen && !hasUnmatchedTheatre
              ? `Screen${unmatchedCount > 1 ? "s" : ""}`
              : hasUnmatchedTheatre && !hasUnmatchedScreen
              ? `Theatre${unmatchedCount > 1 ? "s" : ""}`
              : `Screen${unmatchedCount > 1 ? "s" : ""} or Theatre${unmatchedCount > 1 ? "s" : ""}`}
          </h4>
          <div className={stylesUpload.scrollableContainer}>
            <table className={stylesUpload.table}>
              <thead>
                <tr>
                  <th className={stylesUpload.tableHeader}>Row No.</th>
                  <th
                    className={stylesUpload.tableHeader}
                    onClick={async () => {
                      setCopied(true);
                      await CopyTableColumnToClipboard().then(() => {
                        setTimeout(() => {
                          setCopied(false);
                        }, 2000);
                      });
                    }}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "5px",
                    }}
                  >
                    <span>ID</span>
                    <span>{copied ? <Checked /> : <Copy />}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {unmatchedTheatreData.map((data, index) => (
                  <tr key={index}>
                    <td className={stylesUpload.rowNumber}>{data.order}</td>
                    <td className={stylesUpload.invalidId}>
                      {data.request.id || data.request.screen_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
